import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { AppContext } from '../contexts/AppContext';
import Header from '../framework/Header';
import BackgroundImage from '../components/BackgroundImage';
import PageTransition from '../components/PageTransition';
import ErrorMessageView from '../components/ErrorMessage';
import Loader from '../components/Loader';
import DMObjectModal, { DMObjectPreview } from '../components/DMObjectModal';
import DMResultCard from '../components/DMResultCard';
import DMToolbar, { DMDropdown } from '../components/DMToolbar';
import DMSingleObject from '../components/DMSingleObject';
import DMArticle from '../components/DMArticle';
import ScrollIndicator from '../components/ScrollIndicator';
import { CalendarIcon, LayersIcon, MuseumIcon, SortIcon, TagIcon } from '../components/DMIcons';
import useFetchDocument from '../hooks/useFetchDocument';
import useDMSearch, { useDMOwnerNames } from '../hooks/useDMSearch';
import { DMNameRule, useDMLocalizedName, useDMLocalizedNames } from '../hooks/useDMNameRules';
import { usePlausiblePageview } from '../hooks/usePlausibleTracker';
import { getTranslation } from '../common/Translation';
import { getReadablePalette, ReadablePalette, withImageBackground } from '../common/Color';
import { u } from '../common/Units';
import {
  buildDMBaseSearch, buildDMSubContextSearch, DMContext, DMItemWithOwner, DMUserFilters, DM_DEFAULT_SORT_KEY, DM_FILTER_KEYS, DM_USER_SORT_KEYS,
  getDMSortQuery, isDMArticle, opensAsDMPage, resolveDMCollections, resolveDMContext, resolveDMContextByUniqueId, resolveDMSelection
} from '../services/DigitaltMuseum';

/**
 * The whole page scrolls, including the header and the toolbar. The native scrollbar is hidden (index.css);
 * with accessibility.showScrollbars a ScrollIndicator is shown on top of the content instead
 */
const PageViewport = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  /* Reaches the bottom of the screen, so the bottom menu lies over the results (as in articles) */
  bottom: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  z-index: 1;
  /* Header height + space above the toolbar */
  padding-top: ${u(139)};
  box-sizing: border-box;
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/** The page content below the header. Fades in like PageTransition, without its transform */
const Content = styled.div`
  height: 100%;

  &.visible {
    animation: ${fadeIn} 1.5s ease-in forwards;
  }
`;

const ResultGrid = styled.ul<{ $columns?: number, $gap: number }>`
  list-style: none;
  margin: 0;
  padding: 0 ${u(65)} ${u(60)};
  display: grid;
  grid-template-columns: repeat(${p => p.$columns || 5}, minmax(0, 1fr));
  gap: ${p => p.$gap}px;

  @media (orientation: portrait) {
    grid-template-columns: repeat(${p => p.$columns || 3}, minmax(0, 1fr));
  }
`;

const StatusMessage = styled.div<{ $palette: ReadablePalette }>`
  padding: ${u(40)} ${u(65)};
  text-align: center;
  color: ${p => p.$palette.mutedTextColor};

  & button {
    margin-top: ${u(20)};
    padding: ${u(14)} ${u(28)};
    border-radius: ${u(28)};
    border: 1px solid ${p => p.$palette.borderColor};
    background: transparent;
    color: ${p => p.$palette.textColor};
    font: inherit;
    cursor: pointer;
  }
`;

/** Height of the bottom menu, which lies over the page (0 without a bottom menu) */
const MENU_SPACE = "var(--menu-bottom-space, 0px)";

/**
 * Space below a single object: the same as on the sides, or a small gap above the bottom menu, which already
 * has room around its buttons
 */
const SINGLE_VIEW_BOTTOM = `max(${u(12)}, ${u(65)} - ${MENU_SPACE} * 10)`;

/** Area for a single object or virtual experience. Kept clear of the bottom menu */

const SingleView = styled.div`
  position: relative;
  height: calc(100% - ${SINGLE_VIEW_BOTTOM} - ${MENU_SPACE});
  margin: 0 ${u(65)};

  @media (orientation: portrait) {
    height: auto;
    min-height: calc(100% - ${SINGLE_VIEW_BOTTOM} - ${MENU_SPACE});
  }
`;

/** Last in the content. As tall as the bottom menu, so the last results can be scrolled up from under it */
const Sentinel = styled.div`
  height: max(1px, ${MENU_SPACE});
`;

interface DMSearchViewProps {
  previewData?: any,
  overrideTitle?: string
}

/**
 * Show content from DigitaltMuseum (presentationType "dm"). `dmcontent.contentType` decides what is shown:
 * - "dynamic": a search (`dmcontent.search`), optionally within exhibitions/folders (`dmcontent.collections`)
 * - "manual": a hand-picked selection (`dmcontent.objects`), in the CMS order unless sorted on screen
 * - "single": one object (`dmcontent.object`), shown on its own (object, article or virtual experience).
 *   An exhibition or folder is shown as a list of its objects
 * The CMS content is the base of everything shown; filters chosen on screen only narrow it down.
 * Display settings are in `content.settings` (`sort`, `toolbar`, `grid`).
 * Exhibitions, folders and virtual experiences in the list open as a new route (/dm/:documentId/:contextId).
 * In preview this happens inside the view, since leaving /preview would lose the unsaved preview data.
 * @returns {JSX.Element} Component template
 */
const DMSearchView: FC<DMSearchViewProps> = ({ previewData = null, overrideTitle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documentId, contextId: routeContextId, deviceId: deviceIdFromUrl } = useParams<{ documentId: string, contextId?: string, deviceId?: string }>();
  const { deviceId, deviceData, currentLanguage, theme, previewIsActive } = useContext(AppContext);
  const [data, error] = useFetchDocument(documentId, previewData);
  const [context, setContext] = useState<DMContext | null | undefined>(undefined);
  const [contextError, setContextError] = useState<boolean>(false);
  const [filters, setFilters] = useState<DMUserFilters>({});
  // Sort chosen on screen. null = the default sort from the CMS
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [selectedObject, setSelectedObject] = useState<DMObjectPreview | null>(null);
  // Color behind an article's main image, which lies behind the header
  const [heroColor, setHeroColor] = useState<string | undefined>(undefined);
  // Exhibitions, folders and virtual experiences opened in preview. The back button goes up one level
  const [previewContextIds, setPreviewContextIds] = useState<string[]>([]);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  // The scroll container as state, so the scroll indicator gets it once it is rendered
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const setViewportRef = useCallback((element: HTMLDivElement | null) => {
    viewportRef.current = element;
    setViewport(element);
  }, []);
  const sentinelRef = useRef<HTMLDivElement>(null);

  usePlausiblePageview({ deviceId: String(deviceId), documentId: documentId, documentType: 'dm', ownerId: data?.owner_id, ownerSlug: data?.owner_slug, language: currentLanguage }, { waitFor: ['ownerSlug'] });

  const content = data?.content;
  const dmcontent = content?.dmcontent;
  const settings = content?.settings;
  const sortSetting: string | undefined = settings?.sort;
  const contextId: string | undefined = routeContextId || previewContextIds[previewContextIds.length - 1];
  const contentType: "dynamic" | "manual" | "single" = dmcontent?.contentType || "dynamic";
  const search = dmcontent?.search;
  // Kulturio media IDs (type "dm") of the collections, the hand-picked objects or the single object
  const mediaIds: (number | string)[] = (
    contentType === "single" ? [dmcontent?.object]
    : contentType === "manual" ? dmcontent?.objects || []
    : dmcontent?.collections || []
  ).map((media: any) => media?.mediaId).filter(Boolean);
  const mediaIdsKey = mediaIds.join(",");
  const backgroundColor = content?.background?.backgroundColor || theme?.backgroundColor;
  // settings.display: background behind images (cards and popup), and how images fill the cards
  const imageBackgroundColor: string | undefined = settings?.display?.backgroundColor || undefined;
  const cardObjectFit = settings?.display?.objectFit === "contain" ? "contain" : "cover";
  const palette = useMemo(() => withImageBackground(getReadablePalette(backgroundColor, theme?.backgroundColor), imageBackgroundColor),
    [backgroundColor, theme?.backgroundColor, imageBackgroundColor]);

  // The preview sends a new data object on every change, also when the search did not change. The search is
  // compared by content, so title, colors, toolbar etc. do not restart it (or reset the preview navigation)
  const dmcontentKey = JSON.stringify(dmcontent ?? null);

  useEffect(() => {
    setPreviewContextIds([]);
  }, [dmcontentKey]);

  // Reset what the user chose when another list is shown
  useEffect(() => {
    setFilters({});
    setSortKey(null);
    setSelectedObject(null);
  }, [documentId, contextId]);

  // What the search is restricted to: an object opened from a list (contextId), the single object, the
  // hand-picked selection, or the collections of a dynamic search. Null = no restriction
  useEffect(() => {
    let isSubscribed = true;
    setContextError(false);
    if (!contextId && mediaIds.length === 0) {
      // A single object or a selection without objects shows the "not found" message, never an unrestricted search
      setContext(contentType === "dynamic" ? null : undefined);
      setContextError(contentType !== "dynamic");
      return;
    }

    setContext(undefined);
    const options = { allowSingleObject: true };
    const resolve = contextId ? resolveDMContextByUniqueId(contextId, options)
      : contentType === "single" ? resolveDMContext(mediaIds[0], options)
      : contentType === "manual" ? resolveDMSelection(mediaIds)
      : resolveDMCollections(mediaIds);
    resolve.then(result => {
      isSubscribed && setContext(result);
    }).catch(() => {
      // Never fall back to an unrestricted search
      isSubscribed && setContextError(true);
    });
    return () => { isSubscribed = false };
  }, [contextId, contentType, mediaIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // A single object, virtual experience or article is shown on its own, without a search
  const singleObject = context && !context.fq ? context : null;
  const singleArticle = singleObject && isDMArticle(singleObject.subtype) ? singleObject : null;

  // Base search. Undefined while the context is loading, and for single objects
  const base = useMemo(() => {
    // An object opened from an article has no DM settings of its own, only the context
    if ((!dmcontent && !contextId) || context === undefined || singleObject) { return undefined; }
    // An exhibition or folder opened from a list only shows its own objects
    if (contextId) {
      return context ? buildDMSubContextSearch(search, context, sortSetting) : undefined;
    }
    // A hand-picked selection is shown as chosen, without the search settings
    return buildDMBaseSearch(contentType === "manual" ? {} : search, context, sortSetting);
  }, [dmcontentKey, context, contextId, sortSetting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed for random sorting: stable while paging, new for each visit
  const randomSeed = useMemo(() => Math.floor(Math.random() * 1000000), [documentId, contextId]); // eslint-disable-line react-hooks/exhaustive-deps
  const activeSortKey = sortKey || base?.sortKey || DM_DEFAULT_SORT_KEY;
  const sort = base ? getDMSortQuery(activeSortKey, base, randomSeed) : undefined;

  const { items, numFound, isLoading, hasError: searchError, facets, loadMore } = useDMSearch(base, filters, sort, activeSortKey === "manual");
  const ownerNames = useDMOwnerNames(items, facets.owners);

  // Names in the kiosk's language (settings.dmNameRules). No lookups without rules for the current language
  const nameRules: DMNameRule[] | undefined = settings?.dmNameRules;
  const localizedNames = useDMLocalizedNames(items, nameRules);
  const contextName = useDMLocalizedName(contextId ? context?.uuid : undefined, context?.title, nameRules);

  // Start at the top for each new search
  useEffect(() => {
    viewportRef.current && (viewportRef.current.scrollTop = 0);
  }, [base, filters, sort]);

  // Infinite scroll: load more when the end of the list is close. The observer is renewed after each page,
  // so a new page is loaded if the end is still visible
  useEffect(() => {
    if (!sentinelRef.current || !viewportRef.current || isLoading || typeof IntersectionObserver === "undefined") { return; }
    const observer = new IntersectionObserver(entries => {
      entries[0]?.isIntersecting && loadMore();
    }, { root: viewportRef.current, rootMargin: "0px 0px 800px 0px" });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore, isLoading, items.length]);

  /**
   * Open an object: exhibitions, folders and virtual experiences as a page, other objects in the modal
   */
  const openObject = useCallback((item: DMItemWithOwner) => {
    const parentId = documentId || data?.id;
    const currentDeviceId = deviceIdFromUrl || deviceId;
    if (opensAsDMPage(item) && previewIsActive) {
      setPreviewContextIds(ids => [...ids, item.uniqueId]);
    } else if (opensAsDMPage(item) && parentId && currentDeviceId) {
      navigate(`/device/${currentDeviceId}/dm/${parentId}/${item.uniqueId}`);
    } else {
      setSelectedObject({ ...item, owner: item.ownerName || (item.ownerCode && ownerNames[item.ownerCode]) || undefined });
    }
  }, [ownerNames, documentId, data?.id, deviceId, deviceIdFromUrl, previewIsActive, navigate]);

  const closeObject = useCallback(() => setSelectedObject(null), []);

  if (error) { return <ErrorMessageView title={t("presentationNotFound.title")} body={t("presentationNotFound.body")} redirect={true}/> }
  if (data?.status && data?.status !== "published") { return <ErrorMessageView title={t("presentationNotPublished.title")} body={t("presentationNotPublished.body")} redirect={true}/> }

  const typeLabel = (type: string) => t(`dm.types.${type}`, { defaultValue: t(`dm.type.${type}`, { defaultValue: type }) });
  const hasFilters = Boolean(filters.type || filters.owner || filters.period || filters.subject);
  const resetFilters = () => setFilters({});
  const setFilter = (key: keyof DMUserFilters) => (value: string) => setFilters(prev => ({ ...prev, [key]: value || undefined }));

  // Toolbar from the CMS (settings.toolbar.<key>, booleans). Everything is shown unless explicitly false.
  // A filter is only shown when there is something to choose between
  const toolbar = settings?.toolbar || {};
  const filterDropdowns: Record<typeof DM_FILTER_KEYS[number], DMDropdown | null> = {
    objectType: facets.types.length > 1 || filters.type ? {
      id: "type", icon: <TagIcon/>, selected: filters.type, onSelect: setFilter("type"),
      label: filters.type ? typeLabel(filters.type) : t("dm.filter.allTypes"),
      options: [{ value: "", label: t("dm.filter.allTypes") }, ...facets.types.map(f => ({ ...f, label: typeLabel(f.value) }))]
    } : null,
    museum: facets.owners.length > 1 || filters.owner ? {
      id: "owner", icon: <MuseumIcon/>, selected: filters.owner, onSelect: setFilter("owner"),
      label: filters.owner ? (ownerNames[filters.owner] || filters.owner) : t("dm.filter.museum"),
      options: [{ value: "", label: t("dm.filter.allMuseums") }, ...facets.owners.map(f => ({ ...f, label: ownerNames[f.value] || f.value }))]
    } : null,
    period: facets.periods.length > 1 || filters.period ? {
      id: "period", icon: <CalendarIcon/>, selected: filters.period, onSelect: setFilter("period"),
      label: filters.period ? t(`dm.period.${filters.period}`) : t("dm.filter.period"),
      options: [{ value: "", label: t("dm.filter.allPeriods") }, ...facets.periods.map(f => ({ ...f, label: t(`dm.period.${f.value}`) }))]
    } : null,
    subject: facets.subjects.length > 0 || filters.subject ? {
      id: "subject", icon: <LayersIcon/>, selected: filters.subject, onSelect: setFilter("subject"),
      label: filters.subject || t("dm.filter.subject"),
      options: [{ value: "", label: t("dm.filter.allSubjects") }, ...facets.subjects.map(f => ({ ...f, label: f.value }))]
    } : null
  };
  const visibleFilters = DM_FILTER_KEYS.filter(key => toolbar[key] !== false).map(key => filterDropdowns[key]).filter((d): d is DMDropdown => Boolean(d));
  const sortDropdown: DMDropdown | undefined = base && toolbar.sort !== false ? {
    id: "sort", icon: <SortIcon/>, selected: sortKey || undefined, onSelect: value => setSortKey(value || null),
    label: t(`dm.sort.${activeSortKey}`),
    options: [base.sortKey, ...DM_USER_SORT_KEYS.filter(key => key !== base.sortKey)].map((key, i) => ({ value: i === 0 ? "" : key, label: t(`dm.sort.${key}`) }))
  } : undefined;
  const hitCount = toolbar.hitCount !== false ? (numFound !== null ? t("dm.hits", { count: numFound, formatted: numFound.toLocaleString("nb-NO") }) : "") : undefined;
  const showToolbar = Boolean(base) && (DM_FILTER_KEYS.some(key => toolbar[key] !== false) || toolbar.sort !== false || toolbar.hitCount !== false);

  // Articles show their own title. A folder, exhibition or object opened from the view can have a name in the kiosk's language
  const title = singleArticle ? ""
    : contextId ? (contextName?.title || context?.title || "")
    : getTranslation(overrideTitle, currentLanguage) || (content?.general?.showTitle ? getTranslation(content?.general?.title, currentLanguage) : "");
  const isResolvingContext = context === undefined && !contextError;
  const gap = Number(settings?.grid?.gap) || 20;

  return (
    <>
      <PageTransition waitFor={data}>
        <BackgroundImage media={content?.background?.image} backgroundColor={backgroundColor}/>
      </PageTransition>

      {/* Outside PageTransition: its transform would make the header's fixed menu position itself within the page */}
      <PageViewport ref={setViewportRef}>
        <Header title={title} backgroundColor={backgroundColor} textColor={heroColor ? getReadablePalette(heroColor).textColor : palette.textColor}
          onBack={previewContextIds.length > 0 ? () => setPreviewContextIds(ids => ids.slice(0, -1)) : undefined}/>

        <Content className={data ? "visible" : undefined}>

          {showToolbar && <DMToolbar palette={palette} hitCount={hitCount} filters={visibleFilters} onReset={hasFilters ? resetFilters : undefined} sort={sortDropdown}/>}

          {items.length > 0 && (
            <ResultGrid $columns={Number(settings?.grid?.columns) || undefined} $gap={gap}>
              {items.map(item => (
                <DMResultCard key={item.uniqueId} item={item} localizedName={localizedNames[item.uniqueId]} objectFit={cardObjectFit} ownerName={item.ownerCode && ownerNames[item.ownerCode]} palette={palette} onClick={() => openObject(item)}/>
              ))}
            </ResultGrid>
          )}

          {singleArticle && <DMArticle uuid={singleArticle.uuid || ""} palette={palette} onHeroColor={setHeroColor} onOpenObject={openObject}/>}
          {singleObject && !singleArticle && (
            <SingleView>
              <DMSingleObject object={singleObject} palette={palette} backgroundColor={backgroundColor} imageBackgroundColor={imageBackgroundColor} nameRules={nameRules}/>
            </SingleView>
          )}

          {(isResolvingContext || (isLoading && items.length === 0)) && <Loader variant="page" color={palette.mutedTextColor}/>}
          {data?.content && !dmcontent && !contextId && <StatusMessage $palette={palette}>{t("dm.notConfigured")}</StatusMessage>}
          {contextError && <StatusMessage $palette={palette}>{t("dm.contextNotFound")}</StatusMessage>}
          {searchError && <StatusMessage $palette={palette}>{t("dm.searchError")}</StatusMessage>}
          {!searchError && numFound === 0 && (
            <StatusMessage $palette={palette}>
              <p>{t("dm.noHits")}</p>
              {hasFilters && <button onClick={resetFilters}>{t("dm.filter.reset")}</button>}
            </StatusMessage>
          )}
          {isLoading && items.length > 0 && <Loader variant="inline" color={palette.mutedTextColor}/>}
          <Sentinel ref={sentinelRef}/>
        </Content>
      </PageViewport>

      {deviceData?.accessibility?.showScrollbars && <ScrollIndicator target={viewport}/>}

      {selectedObject && (
        <DMObjectModal uniqueId={selectedObject.uniqueId} preview={selectedObject} backgroundColor={backgroundColor} onClose={closeObject} imageBackgroundColor={imageBackgroundColor} nameRules={nameRules}/>
      )}
    </>
  );
}

export default DMSearchView;
